/**
 * PostApi - Generic POST request wrapper for Lenders App
 * Includes VS-Client-Id header and credentials for cookie-based auth
 *
 * PRIV-20.3: Added optional purpose parameter for explicit audit trail
 */

import { apiResponse } from "./Types";
import { RequestPurpose } from "./GetApi";

function PostApi<returntype>(
  endpoint: string,
  payload: any,
  onSuccess: (data: returntype) => void,
  onFail: (data: object) => void,
  onReject: (response: string) => void,
  purpose?: RequestPurpose // PRIV-20.3: Optional explicit purpose
) {
  // Add debugging to track API calls
  const apiPath = endpoint.replace(import.meta.env.VITE_API_URL || "", "");
  if (import.meta.env.DEV) console.log(`📤 POST API Call: ${apiPath}`, {
    timestamp: new Date().toISOString(),
    endpoint: apiPath,
    fullUrl: endpoint,
    payload: payload,
    purpose: purpose || 'auto-inferred', // PRIV-20.3: Log purpose for debugging
  });

  // PRIV-20.3: Build headers with optional purpose
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "VS-Client-Id": import.meta.env.VITE_VS_CLIENT_ID,
  };
  if (purpose) {
    headers["x-request-purpose"] = purpose;
  }

  const promise = fetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
    headers,
    credentials: "include",
  });

  return promise
    .then(async (putResp) => {
      // Check HTTP status first
      if (!putResp.ok) {
        const errorText = await putResp.text();
        if (import.meta.env.DEV) console.error(`❌ HTTP Error ${putResp.status}:`, errorText);
        throw `HTTP ${putResp.status}: ${errorText}`;
      }

      const jsonData: any = await putResp.json();
      if (
        typeof jsonData.status !== "string" ||
        typeof jsonData.data !== "object"
      ) {
        if (import.meta.env.DEV) console.error("❌ Invalid response format:", jsonData);
        throw await putResp.text();
      }
      return jsonData as apiResponse;
    })
    .then((resp) => {
      const status = resp.status;
      if (status === "success") {
        onSuccess(resp.data as returntype);
      } else if (status === "fail") {
        onFail(resp.data as object);
      } else {
        throw "unexpected response status";
      }
    })
    .catch((error) => {
      if (typeof error === "string") {
        if (import.meta.env.DEV) console.error("❌ PostApi error:", error);
        onReject(error);
      } else {
        if (import.meta.env.DEV) console.error("❌ PostApi unhandled error:", error);
        onReject("unhandled error");
      }
    });
}

export default PostApi;

